import { useContext } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Custom hook for dual event tracking to both Eventable (Snowflake) and Datadog
 * 
 * This hook wraps the existing eventable trackEvent functionality and adds
 * Datadog custom action tracking to ensure events are sent to both systems.
 * 
 * @returns Object with trackEvent function that sends to both systems
 * 
 * @example
 * const { trackEvent } = useDualEventTracking();
 * trackEvent('loan_details_one_time_pay_button_tap', { vehicleId: '123' });
 */
export const useDualEventTracking = () => {
  const { trackEvent: trackEventEventable } = useContext(AnalyticsContext);

  /**
   * Track an event to both Eventable (Snowflake) and Datadog
   * 
   * @param eventName - The name of the event to track
   * @param eventData - Optional data/context associated with the event
   */
  const trackEvent = (eventName: string, eventData?: any) => {
    // CRITICAL: Send to Snowflake first - this MUST always work
    // Wrapped separately so Datadog failures can't affect Snowflake
    try {
      trackEventEventable(eventName, eventData);
    } catch (error) {
      // Log but don't break - Snowflake tracking failed
      console.error('[Snowflake] Failed to track event:', eventName, error);
    }

    // Send to Datadog - completely independent of Snowflake
    // If this fails, Snowflake event was already sent above
    try {
      if (typeof datadogRum !== 'undefined' && datadogRum.addAction) {
        datadogRum.addAction(eventName, eventData || {});
      }
    } catch (error) {
      // Log but don't break - Datadog tracking failed
      console.error('[Datadog] Failed to track event:', eventName, error);
    }
  };

  return { trackEvent };
};

export default useDualEventTracking;
